/**
 * Alta de una **tarea** de mantenimiento.
 *
 * Puerto de `views/maintenance/tasks/create.php`: los desplegables van
 * encadenados (categoría → activo → sub-activo) y el tipo de tarea es texto
 * libre, porque de él depende la regla de «cambio de filtro» que reinicia el
 * contador al aprobar. Aquí se ofrecen como sugerencia los tipos ya usados.
 */

import { useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { formatNumber } from '@/lib/utils';

import type { PrioridadTarea, TareaPayload, TareasCatalogos } from '../tareas.types';

/** Estilo de los controles nativos (mismo que el resto del panel). */
const CONTROL =
  'h-12 w-full rounded-control border border-ink-200 bg-white px-4 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

const MAX_TIPO = 100;

/** Etiqueta de un campo que no usa el componente `Input`. */
function Etiqueta({ htmlFor, children }: { htmlFor: string; children: string }) {
  return (
    <label htmlFor={htmlFor} className="text-sm font-medium text-ink-700">
      {children}
    </label>
  );
}

export function TareaDialogo({
  catalogos,
  guardando,
  error,
  onGuardar,
  onCerrar,
}: {
  catalogos: TareasCatalogos;
  guardando: boolean;
  error: string | null;
  onGuardar: (payload: TareaPayload) => void;
  onCerrar: () => void;
}) {
  const categoriasConActivos = catalogos.categorias.filter(
    (categoria) => categoria.activos.length > 0,
  );

  const [categoriaId, setCategoriaId] = useState(categoriasConActivos[0]?.id ?? 0);
  const categoria = catalogos.categorias.find((item) => item.id === categoriaId);
  const activos = categoria?.activos ?? [];

  const [activoId, setActivoId] = useState(activos[0]?.id ?? 0);
  const activo = activos.find((item) => item.id === activoId);
  const subActivos = activo?.sub_activos ?? [];

  const [subActivoId, setSubActivoId] = useState(subActivos[0]?.id ?? 0);
  const subActivo = subActivos.find((item) => item.id === subActivoId);

  const [tipo, setTipo] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [prioridad, setPrioridad] = useState<PrioridadTarea>('MEDIA');
  const [programada, setProgramada] = useState('');

  const tipoLimpio = tipo.trim();
  const errorTipo =
    tipoLimpio.length === 0
      ? 'El tipo de tarea es obligatorio.'
      : tipoLimpio.length > MAX_TIPO
        ? `Máximo ${MAX_TIPO} caracteres.`
        : null;

  const usado = subActivo ? Math.max(0, subActivo.lectura_actual - subActivo.lectura_cambio) : 0;
  const progreso =
    subActivo && subActivo.umbral > 0
      ? Math.min(100, Math.round((usado / subActivo.umbral) * 100))
      : 0;

  const valido = subActivoId > 0 && !errorTipo;

  return (
    <Dialog
      open
      onClose={onCerrar}
      size="lg"
      title="Nueva tarea de mantenimiento"
      description="La tarea se crea pendiente y sobre un sub-activo: de ahí salen el activo y la categoría."
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onCerrar} disabled={guardando}>
            Cancelar
          </Button>
          <Button
            size="sm"
            loading={guardando}
            disabled={!valido}
            onClick={() =>
              onGuardar({
                sub_activo_id: subActivoId,
                tipo: tipoLimpio,
                descripcion: descripcion.trim(),
                prioridad,
                programada,
              })
            }
          >
            Crear tarea
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <Alert variant="error" title="No se pudo crear">
            {error}
          </Alert>
        ) : null}

        {categoriasConActivos.length === 0 ? (
          <Alert variant="warning" title="No hay sub-activos">
            Para programar una tarea primero registra una categoría, un activo y sus sub-activos.
          </Alert>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-3">
          <div className="flex flex-col gap-1.5">
            <Etiqueta htmlFor="tarea-categoria">Categoría</Etiqueta>
            <Select
              id="tarea-categoria"
              className={CONTROL}
              value={String(categoriaId)}
              onChange={(evento) => {
                const nuevo = Number(evento.target.value);
                const primera = catalogos.categorias.find((item) => item.id === nuevo);
                setCategoriaId(nuevo);
                setActivoId(primera?.activos[0]?.id ?? 0);
                setSubActivoId(primera?.activos[0]?.sub_activos[0]?.id ?? 0);
              }}
            >
              {categoriasConActivos.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.nombre}
                </option>
              ))}
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Etiqueta htmlFor="tarea-activo">Activo</Etiqueta>
            <Select
              id="tarea-activo"
              className={CONTROL}
              value={String(activoId)}
              onChange={(evento) => {
                const nuevo = Number(evento.target.value);
                const primero = activos.find((item) => item.id === nuevo);
                setActivoId(nuevo);
                setSubActivoId(primero?.sub_activos[0]?.id ?? 0);
              }}
            >
              {activos.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.nombre} · {item.codigo}
                </option>
              ))}
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Etiqueta htmlFor="tarea-sub-activo">Sub-activo</Etiqueta>
            <Select
              id="tarea-sub-activo"
              className={CONTROL}
              value={String(subActivoId)}
              onChange={(evento) => setSubActivoId(Number(evento.target.value))}
            >
              {subActivos.length === 0 ? (
                <option value="0">Sin sub-activos</option>
              ) : (
                subActivos.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.nombre} · {item.codigo}
                  </option>
                ))
              )}
            </Select>
          </div>
        </div>

        {subActivo ? (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-card border border-ink-100 bg-ink-50/60 p-3">
            <span className="text-sm text-ink-700">
              Consumo actual: <strong>{formatNumber(usado)}</strong> de{' '}
              <strong>{formatNumber(subActivo.umbral)}</strong> galones
            </span>
            <Badge variant={progreso >= 100 ? 'danger' : progreso >= 80 ? 'warning' : 'success'}>
              {progreso}% del umbral
            </Badge>
          </div>
        ) : null}

        <Input
          label="Tipo de tarea"
          value={tipo}
          maxLength={MAX_TIPO}
          placeholder="Ej. Cambio de filtro"
          list="tarea-tipos-sugeridos"
          autoFocus
          error={tipoLimpio.length > 0 ? (errorTipo ?? undefined) : undefined}
          hint="Si escribes «cambio de filtro», al aprobarla se reinicia el contador del sub-activo"
          onChange={(evento) => setTipo(evento.target.value)}
        />
        <datalist id="tarea-tipos-sugeridos">
          {catalogos.tipos.map((sugerencia) => (
            <option key={sugerencia} value={sugerencia} />
          ))}
        </datalist>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Etiqueta htmlFor="tarea-prioridad">Prioridad</Etiqueta>
            <Select
              id="tarea-prioridad"
              className={CONTROL}
              value={prioridad}
              onChange={(evento) => setPrioridad(evento.target.value as PrioridadTarea)}
            >
              {catalogos.prioridades.map((valor) => (
                <option key={valor} value={valor}>
                  {valor}
                </option>
              ))}
            </Select>
          </div>

          <Input
            label="Fecha programada"
            type="date"
            value={programada}
            hint="Opcional"
            onChange={(evento) => setProgramada(evento.target.value)}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Etiqueta htmlFor="tarea-descripcion">Descripción</Etiqueta>
          <textarea
            id="tarea-descripcion"
            rows={3}
            value={descripcion}
            placeholder="Qué hay que hacer y con qué detalle"
            className="w-full rounded-control border border-ink-200 bg-white px-4 py-3 text-sm text-ink-900 placeholder:text-ink-400 outline-none transition-all duration-200 focus:border-brand-400 focus:ring-4 focus:ring-brand-100"
            onChange={(evento) => setDescripcion(evento.target.value)}
          />
        </div>
      </div>
    </Dialog>
  );
}
